import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import './create-account.form.scss';
import { ReferenceDto } from '../../../services/dtos/reference.dto';
import SelectForm from '../../commun/select-form';
import InputForm from '../../commun/input-form';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';

//Props d'entrée de la fonction CreateAccountForm
type Props = {
    // Liste des civilités
    civilities: ReferenceDto[];
    // Tooltip des critères de mot de passe
    infosPassword: JSX.Element | null;
    // Tooltip des critères d'adresse-mail
    infosMailPerso: JSX.Element;
    // Tooltip pour le champ identifiant
    infosIdentifiant: JSX.Element;
    // Gestion de l'édition du champ mot de passe
    handlerOnChangePassword: (password: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    // Le type d'interlocuteur est-il moral ?
    isMoral: boolean;
    // Gestion du loading lors de l'envoi
    validate: boolean;
    // Gestion du changement d'interlocuteur
    handleChangeInterlocutor: (interlocutor: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    // Liste des types d'interlocuteur
    interlocutorsSelection: { value: string; label: string }[];
};

/**
 * Fonction qui affiche la vue
 * @param civilities liste des civilités
 * @param interlocutors liste des interlocuteurs
 * @param handlerOnChangePassword handler sur le changement de mot de passe
 * @param infosPassword message tooltip
 * @constructor
 */
const CreateAccountViewForm: React.FC<Props> = ({
    civilities,
    handlerOnChangePassword,
    infosPassword,
    infosMailPerso,
    infosIdentifiant,
    isMoral,
    validate,
    handleChangeInterlocutor,
    interlocutorsSelection,
}) => {
    // etat de la visibilité du mot de passe
    const [showPassword, setShowPassword] = React.useState(false);

    // mise à jour de la visibilité du mot de passe
    const handleClickShowPassword = () => setShowPassword(!showPassword);

    // même principe, mais pour l'input de confirmation
    const [showPasswordConfirmation, setShowPasswordConfirmation] = React.useState(false);

    const handleClickShowPasswordConfirmation = () => setShowPasswordConfirmation(!showPasswordConfirmation);

    //Retourne la vue grid avec tous les champs
    //Pour une PP : Affichage des champs nomNaissance, nomNaissance, prenom
    //Pour un PM : il n'y a pas de champ supplémentaire
    const autoCompleteFamilyName = 'family-name';
    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                {/*champ interlocuteur*/}
                <SelectForm
                    required={true}
                    values={interlocutorsSelection}
                    handleChange={handleChangeInterlocutor}
                    id="interlocutors"
                    name="utilisateur.type"
                    label="Vous êtes un/une :"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                {/*champ identifiant*/}
                <InputForm
                    required
                    id="identifiant"
                    name="identifiant"
                    label="Identifiant"
                    type="email"
                    autoComplete={'email'}
                    tooltipMessage={infosIdentifiant}
                />
            </Grid>
            {/*is != PM : champ civilities*/}
            {isMoral ? (
                ''
            ) : (
                <Grid item xs={12} md={6}>
                    <SelectForm
                        required={true}
                        values={civilities}
                        id="civilities"
                        name="utilisateur.civilite"
                        label="Civilité"
                    />
                </Grid>
            )}
            {/*is PM : champ name + grid vide*/}
            {isMoral ? (
                <>
                    <Grid item xs={12} md={6}>
                        <InputForm
                            required
                            id="name"
                            name="utilisateur.nomUsage"
                            label="Nom"
                            autoComplete={autoCompleteFamilyName}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} />
                </>
            ) : (
                <Grid item xs={12} md={6}>
                    <InputForm
                        required
                        id="name"
                        name="utilisateur.nomUsage"
                        label="Nom"
                        autoComplete={autoCompleteFamilyName}
                    />
                </Grid>
            )}
            {/*is != PM : champ civilite*/}
            {/*is != PM : champ firstname*/}
            {/*is != PM : champ brithDate*/}
            {isMoral ? (
                ''
            ) : (
                <>
                    <Grid item xs={12} md={6}>
                        <InputForm
                            id="birthName"
                            name="utilisateur.nomNaissance"
                            label="Nom de naissance"
                            autoComplete={autoCompleteFamilyName}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <InputForm
                            required
                            id="firstname"
                            name="utilisateur.prenom"
                            label="Prénom"
                            autoComplete={'given-name'}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <InputForm
                            maxDate={new Date().toISOString().split('T')[0]}
                            required
                            id="brithDate"
                            name="utilisateur.dateNaissance"
                            label="Date de naissance"
                            type="date"
                            autoComplete={'bday'}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} />
                </>
            )}
            {/*champ mobilePhone*/}
            <Grid item xs={12} md={6}>
                <InputForm
                    id="mobilePhone"
                    name="utilisateur.telPort"
                    label="Téléphone portable"
                    type="tel"
                    InputProps={{
                        startAdornment: <InputAdornment position="start">0</InputAdornment>,
                    }}
                    autoComplete={'tel-national'}
                />
            </Grid>
            {/*champ telFixe*/}
            <Grid item xs={12} md={6}>
                <InputForm
                    id="phone"
                    name="utilisateur.telFixe"
                    label="Téléphone fixe"
                    type="tel"
                    InputProps={{
                        startAdornment: <InputAdornment position="start">0</InputAdornment>,
                    }}
                    autoComplete={'tel-national'}
                />
            </Grid>

            {/*champ mailPerso*/}
            <Grid item xs={12} md={6}>
                <InputForm
                    id="mailPerso"
                    name="utilisateur.mailPerso"
                    label="Email personnel"
                    type="email"
                    autoComplete={'email'}
                    tooltipMessage={infosMailPerso}
                />
            </Grid>
            <Grid item xs={12} md={6} />
            {/*champ mailPro*/}
            <Grid item xs={12} md={6} sx={{ display: 'none' }}>
                <InputForm
                    id="mailPro"
                    name="utilisateur.mailPro"
                    label="Email professionnel"
                    type="email"
                    autoComplete={'email'}
                />
            </Grid>
            {/*champ password*/}
            <Grid item xs={12} md={6}>
                <InputForm
                    required
                    id="password"
                    name="motDePasse"
                    label="Mot de passe"
                    autoComplete={'new-password'}
                    tooltipMessage={infosPassword}
                    handleChange={handlerOnChangePassword}
                    type={showPassword ? 'text' : 'password'}
                    // Gestion de l'affichage du mot de passe
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    data-testid="new-eye-password"
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>
            {/*champ confirmPassword*/}
            <Grid item xs={12} md={6}>
                <InputForm
                    required
                    id="confirmPassword"
                    name="confirmationMotDePasse"
                    label="Confirmation du mot de passe"
                    autoComplete={'new-password'}
                    type={showPasswordConfirmation ? 'text' : 'password'}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    data-testid="new-confirmation-eye-password"
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPasswordConfirmation}
                                    edge="end"
                                >
                                    {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>
            {/*Bouton annuler et valider*/}
            <Grid item xs={0} sm={8} />
            <Grid item xs={12} sm={4} className={'buttons-form'}>
                <Typography variant={'button'}>
                    <Button variant="neutral" component={Link} to={'/'}>
                        Annuler
                    </Button>
                </Typography>
                <Typography variant={'button'}>
                    <Button type="submit" variant="contained" disabled={validate}>
                        Valider
                    </Button>
                </Typography>
            </Grid>
        </Grid>
    );
};

export default CreateAccountViewForm;
